import { Box, Typography } from '@mui/material';

import mt01 from 'assets/img/osperb/milestones/mt-01.jpg';
import mt02 from 'assets/img/osperb/milestones/mt-02.jpg';
import mt03 from 'assets/img/osperb/milestones/mt-03.jpg';
import mt04 from 'assets/img/osperb/milestones/mt-04.jpg';
import mt05 from 'assets/img/osperb/milestones/mt-05.jpg';
import mt06 from 'assets/img/osperb/milestones/mt-06.jpg';
import mt07 from 'assets/img/osperb/milestones/mt-07.jpg';
import mt08 from 'assets/img/osperb/milestones/mt-08.jpg';
import mt09 from 'assets/img/osperb/milestones/mt-09.jpg';

import mt10 from 'assets/img/osperb/milestones/mt-10.jpg';
import mt11 from 'assets/img/osperb/milestones/mt-11.jpg';
import mt12 from 'assets/img/osperb/milestones/mt-12.jpg';
import mt13 from 'assets/img/osperb/milestones/mt-13.jpg';
import mt14 from 'assets/img/osperb/milestones/mt-14.jpg';
import mt15 from 'assets/img/osperb/milestones/mt-15.jpg';
import mt16 from 'assets/img/osperb/milestones/mt-16.jpg';
import mt17 from 'assets/img/osperb/milestones/mt-17.jpg';
import mt18 from 'assets/img/osperb/milestones/mt-18.jpg';
import mt19 from 'assets/img/osperb/milestones/mt-19.jpg';
import mt20 from 'assets/img/osperb/milestones/mt-20.jpg';
import mt21 from 'assets/img/osperb/milestones/mt-21.jpg';
import mt22 from 'assets/img/osperb/milestones/mt-22.jpg';
import mt23 from 'assets/img/osperb/milestones/mt-23.jpg';
import mt24 from 'assets/img/osperb/milestones/mt-24.jpg';
import mt25 from 'assets/img/osperb/milestones/mt-25.jpg';

import { useEffect, useRef } from 'react';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

function MilestoneSection({ type }: { type: string }) {
  const row1 = [mt01, mt02, mt03, mt04, mt05, mt06, mt25];
  const row2 = [mt07, mt08, mt09, mt10, mt11, mt12];
  const row3 = [mt13, mt14, mt15, mt16, mt17, mt18];
  const row4 = [mt19, mt20, mt21, mt22, mt23, mt24];

  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth } = container;
    // The total content is tripled, so one set is scrollWidth / 3
    const oneThird = scrollWidth / 3;
    const threshold = 10;

    if (scrollLeft <= threshold) {
      container.scrollLeft = oneThird + scrollLeft;
    } else if (scrollLeft >= 2 * oneThird - threshold) {
      container.scrollLeft = scrollLeft - oneThird;
    }
  };

  const initializeScroll = () => {
    const container = scrollRef.current;
    if (container) {
      const oneThird = container.scrollWidth / 3;
      // Only center if we are effectively at 0 to avoid disruptive jumps
      if (container.scrollLeft < 50 && oneThird > 0) {
        container.scrollLeft = oneThird;
      }
    }
  };

  useEffect(() => {
    initializeScroll();
  }, []);

  const renderMilestone = (image: string, index: number) => (
    <Box
      key={index}
      sx={{
        height: '150px',
        borderRadius: '6px',
        cornerShape: 'squircle', // custom prop is valid
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
        backgroundColor: '#f5f5f5', // subtle background like partner logos might have
      }}
    >
      <Box
        component="img"
        src={image}
        alt={`milestone-${index}`}
        onLoad={initializeScroll}
        sx={{
          height: '100%',
          width: 'auto',
          maxWidth: 'max-content',
        }}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        padding: type === 'web' ? '120px 0px' : '40px 0px',
        position: 'relative',
        textAlign: type === 'web' ? 'center' : 'left',
      }}
    >
      {/* Title */}
      <Box sx={{ px: type === 'web' ? '24px' : '10px' }}>
        <Typography
          variant="h2"
          className="font-satoshi-bold"
          sx={{
            background: 'linear-gradient(270.11deg, #177BFF 23.63%, #0F1C3F 123.03%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: '28px',
            lineHeight: '110%',
            pb: type === 'web' ? 2 : 1,
          }}
        >
          Milestones, Built Together
        </Typography>
        {type !== 'web' && <Typography
          className="font-satoshi-medium"
          sx={{
            color: '#425A6D',
            fontSize: '14px',
            mb: 1,
          }}
        >
          Our Journey With Clients
        </Typography>}
      </Box>

      {/* Image Collage Grid */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          width: '100%',
          mt: type === 'web' ? 5 : 1,
          position: 'relative',
          pb: 1,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          overflowX: 'auto',
        }}
      >
        {[
          [...row1, ...row1, ...row1],
          [...row2, ...row2, ...row2],
          [...row3, ...row3, ...row3],
          [...row4, ...row4, ...row4],
        ].map((row, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              whiteSpace: 'nowrap',
              px: 2,
              display: 'flex',
              gap: '3px',
              mt: '3px',
            }}
          >
            {row.map((image, index) => renderMilestone(image, index))}
          </Box>
        ))}
      </Box>

      {type === 'web' && <Typography
        className="font-satoshi-medium"
        sx={{
          color: '#425A6D',
          fontSize: '14px',
          mb: 1,
        }}
      >
        Our Journey With Clients
      </Typography>}

      {/* Description */}
      {type === 'web' ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            mt: 4,
            pt: 15,
            pb: 5
          }}
        >
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}
          >
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 400,
                fontSize: '192.44px',
                lineHeight: '140px',
                letterSpacing: '-4%',
                color: '#B9B9B9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              85   <Typography
                className='font-sofia'
                sx={{
                  fontWeight: 400,
                  fontSize: '192.44px',
                  lineHeight: '140px',
                  letterSpacing: '-8%',
                  color: '#B9B9B9',
                }}
              >
                %
              </Typography>
            </Typography>

            <Typography
              className="font-satoshi-regular"
              sx={{
                fontSize: '44px',
                color: '#B9B9B9',
                lineHeight: '100%',
                whiteSpace: 'nowrap',
                letterSpacing: '-0.02em',
                mt: 1
              }}
            >
              client retention
            </Typography>
          </Box>
          <Typography
            className="font-satoshi-light"
            sx={{
              color: '#666',
              fontSize: '24px',
              lineHeight: '150%',
              textAlign: 'left',
              maxWidth: '380px',
            }}
          >
            Our 85% client retention rate proves that our long-term technology partnerships really{' '}
            <Box component="span" className="font-satoshi-bold" sx={{ fontWeight: 700, color: '#333' }}>
              deliver consistent results
            </Box>{' '}
            and{' '}
            <Box component="span" className="font-satoshi-bold" sx={{ fontWeight: 700, color: '#333' }}>
              peace of mind
            </Box>{' '}
            for growing businesses
          </Typography>
        </Box>
      ) : (
        <Typography
          className="font-satoshi-light"
          sx={{
            color: '#333',
            fontSize: '24px',
            lineHeight: '150%',
            width: '100%',
            px: '24px',
            mt: 2
          }}
        >
          Our 85% client retention rate proves that our long-term technology partnerships really{' '}
          <br />
          <Box component="span" className="font-satoshi-bold" sx={{ fontWeight: 700 }}>
            deliver consistent results
          </Box>{' '}
          and{' '}
          <Box component="span" className="font-satoshi-bold" sx={{ fontWeight: 700 }}>
            peace of mind
          </Box>{' '}
          for growing businesses
        </Typography>
      )}
    </Box>
  );
}

export default MilestoneSection;
